// The application form of the loan officer one-pager: the borrower one-pager's
// five sections, worded for the loan officer, with the co-borrower as their
// own party, per-borrower declarations, one upload box per borrower and the
// Consumer Connect option. Only the borrower's name, email and phone are
// required.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Form, Input, InputNumber, Radio, Select, Switch, Upload, message } from "antd";
import {
  ArrowRightOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import AddressAutocomplete from "../QuickIntake/AddressAutocomplete";
import * as com from "../Common.js";
import {
  ChoiceCards,
  DownPaymentShare,
  EXAMPLE_DOCUMENTS,
  ficoRanges,
  formatBytes,
  moneyInputProps,
  yesNo,
} from "../QuickIntake/GMCCQuickIntake/GMCCQuickIntake";
import { createLOApplication, processLOApplication, uploadLODocuments } from "./loOnePagerClient";
import { DECLARATION_QUESTIONS, Lang, TranslateLO } from "./loOnePagerI18n";

const { Dragger } = Upload;

const SECTIONS = [
  { id: "lop-borrower", title: "secBorrower" },
  { id: "lop-loan", title: "secLoanLO" },
  { id: "lop-property", title: "secPropertyLO" },
  { id: "lop-declarations", title: "secDeclLO" },
  { id: "lop-documents", title: "secDocsLO", optional: true },
] as const;

const DECLARATION_KEYS = ["A", "B", "C", "D1", "D2", "E", "F", "G", "H", "I", "J", "K", "L", "M"];
const GROUP_KEYS = DECLARATION_KEYS.slice(1);

const filled = (v: any) => v !== undefined && v !== null && `${v}`.trim() !== "";

const formatSSN = (value: string) => {
  const ssn = `${value || ""}`.replace(/[^\d]/g, "");
  if (ssn.length < 4) return ssn;
  if (ssn.length < 6) return `${ssn.slice(0, 3)}-${ssn.slice(3)}`;
  return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5, 9)}`;
};

const maskedSSN = (value: string) => {
  const ssn = `${value || ""}`.replace(/[^\d]/g, "");
  if (!ssn) return "";
  if (ssn.length < 4) return "***";
  if (ssn.length < 6) return `***-${"*".repeat(ssn.length - 3)}`;
  return `***-**-${"*".repeat(Math.min(ssn.length - 5, 4))}`;
};

// A Form.Item control: digits typed, masked when not focused.
const SSNInput: React.FC<{ value?: string; onChange?: (v: string) => void; t: TranslateLO }> = ({ value, onChange, t }) => {
  const [visible, setVisible] = useState(false);
  const [focused, setFocused] = useState(false);
  const shown = focused || visible ? formatSSN(value || "") : maskedSSN(value || "");
  return (
    <div className="gop-ssn">
      <LockOutlined rev={undefined} className="gop-ssn-lock" />
      <Input
        value={shown}
        onChange={(e) => onChange?.(formatSSN(e.target.value))}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="•••-••-••••"
        maxLength={11}
        inputMode="numeric"
        autoComplete="off"
      />
      <button type="button" className="gop-ssn-toggle" aria-label={visible ? t("ssnHide") : t("ssnShow")} onClick={() => setVisible(!visible)}>
        {visible ? <EyeOutlined rev={undefined} /> : <EyeInvisibleOutlined rev={undefined} />}
      </button>
    </div>
  );
};

const sectionState = (v: any, fileCount: number): boolean[] => {
  if (!v) return [false, false, false, false, fileCount > 0];
  const co = !!v.hasCoBorrower;
  const borrower =
    ["firstName", "lastName", "email", "phone"].every((k) => filled(v[k])) && (!co || ["coFirstName", "coLastName"].every((k) => filled(v[k])));
  const loan = v.loanPurpose === "refinance" ? filled(v.remainingBalance) : filled(v.purchasePrice);
  const property = (filled(v.propertyState) && (filled(v.propertyCity) || filled(v.propertyAddress))) || filled(v.propertyUse);
  const declarations = DECLARATION_KEYS.every((k) => filled(v[`declaration_${k}`])) && (!co || DECLARATION_KEYS.every((k) => filled(v[`co_declaration_${k}`])));
  return [borrower, loan, property, declarations, fileCount > 0];
};

interface Props {
  t: TranslateLO;
  lang: Lang;
  onSubmitted: (loanId: string, key: string) => void;
}

const LOApplicationForm: React.FC<Props> = ({ t, lang, onSubmitted }) => {
  const [form] = Form.useForm();
  const [loanPurpose, setLoanPurpose] = useState<string>("purchase");
  const [showCo, setShowCo] = useState(false);
  // The "send the borrower a Consumer Connect link" choice is hidden for now
  // (product decision 2026-09-26); while hidden nothing is sent, so a loan
  // officer is never surprised by an email they had no control over. The
  // backend keeps honouring the flag -- flip this to bring the option back.
  const SHOW_CONSUMER_CONNECT_OPTION = false;
  const [sendCC, setSendCC] = useState(SHOW_CONSUMER_CONNECT_OPTION);
  const [loanLimits, setLoanLimits] = useState<any>({});
  const [selectedState, setSelectedState] = useState("");
  const [addressValue, setAddressValue] = useState("");
  const addressValueRef = useRef("");
  const [fileList, setFileList] = useState<any[]>([]);
  const [coFileList, setCoFileList] = useState<any[]>([]);
  const [declParty, setDeclParty] = useState<"" | "co_">("");
  const [declOpen, setDeclOpen] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState(0);

  const values = Form.useWatch([], form) || {};
  const firstName = values.firstName || "";
  const lastName = values.lastName || "";
  const coFirstName = values.coFirstName || "";
  const coLastName = values.coLastName || "";
  const borrowerName = `${firstName} ${lastName}`.trim() || t("roleBorrower");
  const coName = `${coFirstName} ${coLastName}`.trim() || t("roleCoborrower");

  useEffect(() => {
    fetch("/data/loanlimits")
      .then((r) => (r.ok ? r.json() : {}))
      .then(setLoanLimits)
      .catch(() => {});
  }, []);

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        let current = 0;
        SECTIONS.forEach((s, i) => {
          const el = document.getElementById(s.id);
          if (el && el.getBoundingClientRect().top <= 160) current = i;
        });
        setActive(current);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  const stateOptions = useMemo(
    () =>
      Object.keys(loanLimits || {}).map((x) => ({ value: com.capitalize(x), label: com.capitalize(x) })),
    [loanLimits]
  );
  const countyOptions = useMemo(() => {
    if (!selectedState || !loanLimits) return [];
    let s = selectedState.toUpperCase();
    if (selectedState.length === 2) s = com.convertAbbStateToFull(selectedState).toUpperCase();
    if (!(s in loanLimits)) return [];
    return Object.keys(loanLimits[s]).map((x) => ({ value: com.capitalize(x), label: com.capitalize(x) }));
  }, [selectedState, loanLimits]);

  const done = sectionState(values, fileList.length + coFileList.length);
  const doneCount = done.filter(Boolean).length;

  const validatePhone = (_: any, value: string) => {
    if (value && !/^\(\d{3}\) \d{3}-\d{4}$/.test(value)) return Promise.reject(new Error(t("badPhone")));
    return Promise.resolve();
  };
  const validateSSN = (_: any, value: string) => {
    if (value && value.replace(/[^\d]/g, "").length !== 9) return Promise.reject(new Error(t("badSsn")));
    return Promise.resolve();
  };

  const makeDragger = (list: any[], setList: (l: any[]) => void) => ({
    fileList: list,
    multiple: true,
    showUploadList: false,
    customRequest: ({ onSuccess }: any) => onSuccess?.("ok"),
    onChange(info: any) {
      setList([...info.fileList]);
    },
    beforeUpload: (file: any) => {
      if (/\.(heic|heif)$/i.test(file?.name || "")) {
        message.warning(t("onlyImagesPdf"));
        return Upload.LIST_IGNORE;
      }
      return true;
    },
  });

  const removeAt = (list: any[], setList: (l: any[]) => void, index: number) => {
    const next = [...list];
    next.splice(index, 1);
    setList(next);
  };

  // ---------- declarations ----------

  const declValue = (prefix: string, k: string) => values[`${prefix}declaration_${k}`];
  const groupSummary = (prefix: string) => {
    const answers = GROUP_KEYS.map((k) => declValue(prefix, k));
    const yes = answers.filter((a) => a === "yes").length;
    const no = answers.filter((a) => a === "no").length;
    if (no === GROUP_KEYS.length) return t("allNo");
    if (yes + no === 0) return t("notAnsweredYet", { n: GROUP_KEYS.length });
    return t("groupSummary", { yes, no, left: GROUP_KEYS.length - yes - no });
  };
  const answeredCount = (prefix: string) => DECLARATION_KEYS.filter((k) => filled(declValue(prefix, k))).length;
  const markAllNo = (prefix: string) => {
    const patch: Record<string, string> = {};
    GROUP_KEYS.forEach((k) => (patch[`${prefix}declaration_${k}`] = "no"));
    form.setFieldsValue(patch);
  };

  const yesNoGroup = (name: string, extra: any = {}) => (
    <Form.Item name={name} noStyle>
      <Radio.Group optionType="button" className="gop-seg" options={yesNo(t)} {...extra} />
    </Form.Item>
  );

  const declarationsFor = (prefix: "" | "co_") => {
    const questions = DECLARATION_QUESTIONS[lang];
    const open = !!declOpen[prefix];
    const aYes = declValue(prefix, "A") === "yes";
    const a1Yes = values[`${prefix}declaration_A1`] === "yes";
    const cYes = declValue(prefix, "C") === "yes";
    return (
      <>
        <div className="gop-decl">
          <div className="gop-decl-row">
            <span className="gop-decl-letter">A</span>
            <span className="gop-decl-q">
              <span>{t("declALO")}</span>
            </span>
            <div className="gop-decl-answer">{yesNoGroup(`${prefix}declaration_A`)}</div>
          </div>
          {aYes ? (
            <div className="gop-followup">
              <div className="gop-inline-question">
                <span className="gop-inline-question-label">{t("ownedAnother")}</span>
                {yesNoGroup(`${prefix}declaration_A1`)}
              </div>
              {a1Yes ? (
                <div className="gop-grid">
                  <Form.Item label={t("ownedType")} name={`${prefix}declaration_A1_Type`}>
                    <Select
                      placeholder={t("select")}
                      options={[
                        { value: "primary", label: t("usePrimary") },
                        { value: "secondary", label: t("typeSecondary") },
                        { value: "investment", label: t("typeInvestment") },
                        { value: "fha", label: t("useFha") },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item label={t("titleHeld")} name={`${prefix}declaration_A1_Status`}>
                    <Select
                      placeholder={t("select")}
                      options={[
                        { value: "by-yourself", label: t("titleSole") },
                        { value: "jointly-with-spouse", label: t("titleSpouse") },
                        { value: "jointly-with-other", label: t("titleOther") },
                      ]}
                    />
                  </Form.Item>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="lop-decl-group">
          <div className="lop-decl-group-head">
            <div>
              <div className="lop-decl-group-title">{t("questionsBM")}</div>
              <div className="lop-decl-group-sub">{groupSummary(prefix)}</div>
            </div>
            <div className="lop-decl-group-actions">
              <button type="button" className="lop-btn-ghost" onClick={() => markAllNo(prefix)}>
                {t("markAllNo")}
              </button>
              <button type="button" className="lop-btn-ghost" onClick={() => setDeclOpen({ ...declOpen, [prefix]: !open })}>
                {open ? t("hide") : t("reviewBtn")}
              </button>
            </div>
          </div>
          {/* Always mounted, hidden when collapsed: antd only reports and submits
              registered fields, so "Mark all No" on a collapsed group would
              otherwise set values nothing reads and the summary would not move. */}
          <div className="lop-decl-group-body" style={{ display: open ? undefined : "none" }}>
              {GROUP_KEYS.map((k, i) => (
                <div key={`${prefix}${k}`} className="gop-decl">
                  <div className="gop-decl-row">
                    <span className="gop-decl-letter">{k}</span>
                    <span className="gop-decl-q">
                      <span>{questions[i + 1]}</span>
                    </span>
                    <div className="gop-decl-answer">{yesNoGroup(`${prefix}declaration_${k}`)}</div>
                  </div>
                  {k === "C" && cYes ? (
                    <div className="gop-followup">
                      <Form.Item label={t("borrowedAmount")} name={`${prefix}declaration_C1`}>
                        <InputNumber {...moneyInputProps} placeholder={t("amountPlaceholder")} />
                      </Form.Item>
                    </div>
                  ) : null}
                </div>
              ))}
          </div>
        </div>
        <div className="gop-decl-footnote">
          {showCo ? t("markAllNote", { name: prefix === "co_" ? coName : borrowerName }) : t("markAllNoteSingle")}
        </div>
      </>
    );
  };

  // ---------- submit ----------

  const focusField = (name: any) => {
    setTimeout(() => {
      form.scrollToField(name, { behavior: "smooth", block: "center" });
    }, 50);
  };

  const submit = async () => {
    let v: any;
    try {
      v = await form.validateFields();
    } catch (error: any) {
      const firstBad = error?.errorFields?.[0]?.name;
      if (firstBad) focusField(firstBad);
      message.error(t("completeHighlighted"));
      return;
    }
    setSaving(true);
    try {
      const purchase = v.loanPurpose !== "refinance";
      const price = Number(v.purchasePrice || 0);
      const down = Number(v.downPayment || 0);
      const payload: Record<string, any> = {
        firstName: v.firstName,
        lastName: v.lastName,
        email: v.email,
        phone: v.phone,
        ssn: v.ssn || "",
        ficoScore: Number(v.ficoScore || 0),
        hasCoBorrower: !!v.hasCoBorrower,
        coFirstName: v.coFirstName || "",
        coLastName: v.coLastName || "",
        coEmail: v.coEmail || "",
        coPhone: v.coPhone || "",
        coSsn: v.coSsn || "",
        coFicoScore: Number(v.coFicoScore || 0),
        loanPurpose: purchase ? "purchase" : "refinance",
        loanAmount: purchase && price > down ? price - down : 0,
        purchasePrice: purchase ? price : 0,
        downPayment: purchase ? down : 0,
        isFirstTimeBuyer: v.isFirstTimeBuyer || "",
        remainingBalance: Number(v.remainingBalance || 0),
        currentInterestRate: Number(v.currentInterestRate || 0),
        hasSecondMortgage: v.hasSecondMortgage === "yes" ? 1 : 0,
        borrowAdditionalCash: Number(v.borrowAdditionalCash || 0),
        propertyAddress: addressValueRef.current || v.propertyAddress || "",
        propertyUnit: v.propertyUnit || "",
        propertyState: selectedState || v.propertyState || "",
        propertyCounty: v.propertyCounty || "",
        propertyCity: v.propertyCity || "",
        propertyZip: v.propertyZip || "",
        propertyValue: Number(v.propertyValue || 0),
        propertyUse: v.propertyUse || "",
        declaration_A1_Type: v.declaration_A1_Type || "",
        declaration_A1_Status: v.declaration_A1_Status || "",
        declaration_C1: Number(v.declaration_C1 || 0),
        co_declaration_A1_Type: v.co_declaration_A1_Type || "",
        co_declaration_A1_Status: v.co_declaration_A1_Status || "",
        co_declaration_C1: Number(v.co_declaration_C1 || 0),
        sendConsumerConnect: sendCC,
      };
      [...DECLARATION_KEYS, "A1"].forEach((k) => {
        payload[`declaration_${k}`] = v[`declaration_${k}`] || "";
        payload[`co_declaration_${k}`] = v[`co_declaration_${k}`] || "";
      });

      const created = await createLOApplication(payload);
      const loanId = created.loan_id;
      if (fileList.length || coFileList.length) {
        try {
          await uploadLODocuments(
            loanId,
            fileList.map((f) => f.originFileObj).filter(Boolean),
            coFileList.map((f) => f.originFileObj).filter(Boolean)
          );
        } catch (e: any) {
          message.error(e?.message || t("uploadFailedLO"));
        }
      }
      await processLOApplication(loanId);
      onSubmitted(loanId, created.key);
    } catch (e: any) {
      message.error(e?.message || t("createFailed"));
    } finally {
      setSaving(false);
    }
  };

  // ---------- pieces ----------

  const sectionCard = (index: number, title: React.ReactNode, sub: React.ReactNode, body: React.ReactNode, right?: React.ReactNode) => (
    <section id={SECTIONS[index].id} className="gop-section">
      <div className="gop-section-head">
        <span className="gop-section-num">{index + 1}</span>
        <div className="gop-section-titles">
          <div className="gop-section-title">{title}</div>
          {sub ? <div className="gop-section-sub">{sub}</div> : null}
        </div>
        {right}
      </div>
      <div className="gop-section-body">{body}</div>
    </section>
  );

  const dropzone = (props: any) => (
    <Dragger {...props} className="gop-drop">
      <div className="gop-drop-inner">
        <span className="gop-drop-icon">
          <UploadOutlined rev={undefined} />
        </span>
        <span className="gop-drop-text">
          <span className="gop-drop-title">
            <em>{t("chooseFiles")}</em>
            {t("orDragHere")}
          </span>
          <span className="gop-drop-sub">{t("dropSubLO")}</span>
        </span>
      </div>
    </Dragger>
  );

  const fileRows = (list: any[], setList: (l: any[]) => void) =>
    list.length > 0 && (
      <div className="gop-files">
        {list.map((item, index) => (
          <div key={item.uid} className="gop-file">
            <span className="gop-file-icon">
              <UploadOutlined rev={undefined} />
            </span>
            <span className="gop-file-text">
              <span className="gop-file-name">{item.name}</span>
              <span className="gop-file-size">{formatBytes(item.size)}</span>
            </span>
            <span className="gop-file-status">
              <CheckOutlined rev={undefined} />
              {t("ready")}
            </span>
            <button type="button" className="gop-file-remove" aria-label={t("removeFile", { name: item.name })} onClick={() => removeAt(list, setList, index)}>
              <CloseOutlined rev={undefined} />
            </button>
          </div>
        ))}
      </div>
    );

  const examples = (
    <div>
      <div className="gop-examples-title">{t("forExample")}</div>
      <div className="gop-chips">
        {EXAMPLE_DOCUMENTS.map((d) => (
          <span key={d} className="gop-chip">
            {t(d)}
          </span>
        ))}
      </div>
    </div>
  );

  const personFields = (prefix: "" | "co", required: boolean) => {
    const n = (k: string) => (prefix ? `co${k[0].toUpperCase()}${k.slice(1)}` : k);
    return (
      <div className="gop-grid">
        <Form.Item label={t("firstName")} name={n("firstName")} rules={[{ required: true, message: prefix ? t("reqCoFirstName") : t("reqFirstName") }]}>
          <Input placeholder={prefix ? "Sam" : "Jane"} />
        </Form.Item>
        <Form.Item label={t("lastName")} name={n("lastName")} rules={[{ required: true, message: prefix ? t("reqCoLastName") : t("reqLastName") }]}>
          <Input placeholder={prefix ? "Lee" : "Doe"} />
        </Form.Item>
        <Form.Item
          label={t("emailLabel")}
          name={n("email")}
          rules={required ? [{ required: true, message: t("reqEmail") }, { type: "email", message: t("badEmail") }] : [{ type: "email", message: t("badEmail") }]}
        >
          <Input placeholder={prefix ? "sam@email.com" : "jane@email.com"} inputMode="email" />
        </Form.Item>
        <Form.Item
          label={t("mobilePhone")}
          name={n("phone")}
          rules={required ? [{ required: true, message: t("reqPhone") }, { validator: validatePhone }] : [{ validator: validatePhone }]}
          getValueFromEvent={(e) => com.fixPhoneInput(e.target.value)}
        >
          <Input placeholder="(555) 555-5555" maxLength={14} inputMode="tel" />
        </Form.Item>
        <Form.Item
          label={
            <>
              {t("ssn")}
              <span className="gop-label-note">{t("optionalParen")}</span>
            </>
          }
          name={n("ssn")}
          rules={[{ validator: validateSSN }]}
          extra={prefix ? undefined : t("ssnHintLO")}
        >
          <SSNInput t={t} />
        </Form.Item>
        <Form.Item label={t("estimatedFico")} name={n("ficoScore")} extra={prefix ? undefined : t("ficoHint")}>
          <Select placeholder={t("selectRange")} options={ficoRanges(t)} allowClear />
        </Form.Item>
      </div>
    );
  };

  return (
    <div className="gop-layout lop-form-layout">
      <aside className="gop-aside">
        <div className="gop-panel">
          <div className="gop-eyebrow">{t("application")}</div>
          <div className="gop-meter">
            <div className="gop-meter-fill" style={{ width: `${(doneCount / SECTIONS.length) * 100}%` }} />
          </div>
          <div className="gop-meter-text">{t("sectionsComplete", { done: doneCount, total: SECTIONS.length })}</div>
          <nav className="gop-nav">
            {SECTIONS.map((s, i) => {
              const state = i === active ? "is-active" : done[i] ? "is-done" : "";
              return (
                <button
                  type="button"
                  key={s.id}
                  className={`gop-nav-item ${state}`}
                  onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                >
                  <span className="gop-nav-dot">{state === "is-done" ? <CheckOutlined rev={undefined} /> : i + 1}</span>
                  {t(s.title)}
                  {"optional" in s && s.optional ? <span className="gop-nav-optional">{t("optional")}</span> : null}
                </button>
              );
            })}
          </nav>
        </div>
        {SHOW_CONSUMER_CONNECT_OPTION ? (
          <div className="gop-panel">
            <div className="gop-eyebrow">{t("afterYouSync")}</div>
            <label className="lop-after-sync">
              <Checkbox checked={sendCC} onChange={(e) => setSendCC(e.target.checked)} />
              <div>
                <strong>{t("sendCC")}</strong>
                <span>{t("sendCCSub")}</span>
              </div>
            </label>
          </div>
        ) : null}
      </aside>

      <main className="gop-main">
        <div className="gop-intro">
          <div className="gop-title" role="heading" aria-level={1}>
            {t("newApplication")}
          </div>
          <p style={{ color: "#52525b", marginTop: 6 }}>{t("formIntro")}</p>
        </div>

        <Form
          form={form}
          layout="vertical"
          initialValues={{ loanPurpose: "purchase" }}
          requiredMark={(label, { required }) => (
            <>
              {label}
              {required && <span className="gop-req">*</span>}
            </>
          )}
        >
          {sectionCard(
            0,
            t("secBorrower"),
            t("borrowerSub"),
            <>
              {personFields("", true)}
              <div className="gop-toggle-row" onClick={() => { const next = !showCo; setShowCo(next); form.setFieldsValue({ hasCoBorrower: next }); }}>
                <div>
                  <div className="gop-toggle-title">{t("coToggleTitleLO")}</div>
                  <div className="gop-toggle-sub">{t("coToggleSubLO")}</div>
                </div>
                <Form.Item name="hasCoBorrower" valuePropName="checked" noStyle>
                  <Switch onClick={(_c, e) => e.stopPropagation()} onChange={(checked) => setShowCo(checked)} />
                </Form.Item>
              </div>
              {showCo ? (
                <div className="lop-co-block">
                  <div className="lop-co-block-head">
                    <span className="lop-pill">{t("roleCoborrower")}</span>
                    <span>{t("coPartyNote")}</span>
                  </div>
                  {personFields("co", false)}
                </div>
              ) : null}
            </>
          )}

          {sectionCard(
            1,
            t("secLoanLO"),
            t("loanSubLO"),
            <>
              <Form.Item label={t("loanPurpose")} name="loanPurpose">
                <ChoiceCards onPick={(v: string) => setLoanPurpose(v)} />
              </Form.Item>
              {loanPurpose === "purchase" ? (
                <>
                  <div className="gop-grid">
                    <Form.Item label={t("purchasePrice")} name="purchasePrice">
                      <InputNumber {...moneyInputProps} placeholder="750,000" />
                    </Form.Item>
                    <div className="gop-dp">
                      <Form.Item
                        label={t("downPayment")}
                        name="downPayment"
                        dependencies={["purchasePrice"]}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const price = Number(getFieldValue("purchasePrice") || 0);
                              if (value && price && Number(value) > price) return Promise.reject(new Error(t("downPaymentTooBig")));
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <InputNumber {...moneyInputProps} placeholder="150,000" />
                      </Form.Item>
                      <DownPaymentShare form={form} />
                    </div>
                  </div>
                  <div className="gop-inline-question">
                    <span className="gop-inline-question-label">{t("firstTimeQ")}</span>
                    {yesNoGroup("isFirstTimeBuyer")}
                  </div>
                </>
              ) : (
                <>
                  <div className="gop-grid">
                    <Form.Item label={t("remainingBalance")} name="remainingBalance">
                      <InputNumber {...moneyInputProps} placeholder="450,000" />
                    </Form.Item>
                    <Form.Item label={t("currentRate")} name="currentInterestRate">
                      <InputNumber min={0} max={100} step={0.01} controls={false} suffix="%" placeholder="6.5" stringMode />
                    </Form.Item>
                    <Form.Item label={t("cashOut")} name="borrowAdditionalCash">
                      <InputNumber {...moneyInputProps} placeholder="0" />
                    </Form.Item>
                  </div>
                  <div className="gop-inline-question">
                    <span className="gop-inline-question-label">{t("secondMortgage")}</span>
                    {yesNoGroup("hasSecondMortgage")}
                  </div>
                </>
              )}
            </>
          )}

          {sectionCard(
            2,
            t("secPropertyLO"),
            t("propertySubLO"),
            <>
              <div className="gop-address-row">
                <div className="gop-address">
                  <SearchOutlined rev={undefined} className="gop-address-icon" />
                  <Form.Item label={t("streetAddress")} name="propertyAddress" extra={t("streetExtra")}>
                    <AddressAutocomplete
                      id="LOPropertyAddress"
                      required={false}
                      border={true}
                      defaultValue={addressValue}
                      hideLabel={true}
                      onChange={(e: any) => {
                        addressValueRef.current = e.target.value;
                        if (!e.target.value) {
                          form.setFieldsValue({ propertyState: undefined, propertyCounty: undefined, propertyCity: "", propertyZip: "" });
                          setSelectedState("");
                        }
                      }}
                      pattern="^[0-9]+\\s[\\p{L}\\p{N} !:,&()'?#+\\/@-]+$"
                      placeholder={t("addressPlaceholder")}
                      goodfeedback={t("addressGood")}
                      badfeedback={t("addressBad")}
                      onCompletion={(address: any) => {
                        const state = com.getSubaddress(address, "administrative_area_level_1", "short_name");
                        const zip = com.getSubaddress(address, "postal_code", "long_name");
                        const county = com.getSubaddress(address, "administrative_area_level_2", "long_name");
                        const num = com.getSubaddress(address, "street_number", "long_name");
                        const short = com.getSubaddress(address, "route", "short_name");
                        const city = com.getSubaddress(address, "locality", "long_name");
                        const stradd = `${num} ${short}, ${city}, ${state} ${zip}`;
                        const fullState = com.capitalize(com.convertAbbStateToFull(state));
                        setSelectedState(fullState);
                        setAddressValue(stradd);
                        addressValueRef.current = stradd;
                        form.setFieldsValue({ propertyState: fullState, propertyCounty: county, propertyCity: city, propertyZip: zip, propertyAddress: stradd });
                      }}
                      size="md"
                    />
                  </Form.Item>
                </div>
                <Form.Item label={t("unit")} name="propertyUnit">
                  <Input placeholder={t("unitPlaceholder")} />
                </Form.Item>
              </div>
              <div className="gop-grid gop-grid-4">
                <Form.Item label={t("city")} name="propertyCity">
                  <Input placeholder={t("city")} />
                </Form.Item>
                <Form.Item label={t("state")} name="propertyState">
                  <Select
                    placeholder={t("select")}
                    showSearch
                    optionFilterProp="label"
                    options={stateOptions}
                    onChange={(value: string) => {
                      setSelectedState(value);
                      form.setFieldsValue({ propertyCounty: undefined });
                    }}
                  />
                </Form.Item>
                <Form.Item label={t("county")} name="propertyCounty">
                  <Select placeholder={t("select")} showSearch optionFilterProp="label" options={countyOptions} />
                </Form.Item>
                <Form.Item label={t("zip")} name="propertyZip">
                  <Input placeholder="00000" inputMode="numeric" />
                </Form.Item>
              </div>
              <div className="gop-grid">
                <Form.Item label={t("propertyValue")} name="propertyValue">
                  <InputNumber {...moneyInputProps} placeholder="750,000" />
                </Form.Item>
                <Form.Item label={t("occupancy")} name="propertyUse">
                  <Select
                    placeholder={t("select")}
                    allowClear
                    options={[
                      { value: "primary", label: t("usePrimary") },
                      { value: "secondary", label: t("useSecond") },
                      { value: "investment", label: t("useInvestment") },
                      { value: "fha", label: t("useFha") },
                    ]}
                    onChange={(value: string) => {
                      // Declaration A asks the same thing; the server derives it
                      // from here regardless. Keep the visible answer in step.
                      const a = value ? (value === "primary" ? "yes" : "no") : undefined;
                      form.setFieldsValue({ declaration_A: a, co_declaration_A: a });
                    }}
                  />
                </Form.Item>
              </div>
            </>
          )}

          {sectionCard(
            3,
            t("secDeclLO"),
            t("declSubLO"),
            <>
              {showCo ? (
                <div className="lop-party-tabs" style={{ marginBottom: 12 }}>
                  <button type="button" className={`lop-party-tab${declParty === "" ? " is-on" : ""}`} onClick={() => setDeclParty("")}>
                    {borrowerName} <small>· {t("roleBorrower")}</small> <small>{answeredCount("")}/14</small>
                  </button>
                  <button type="button" className={`lop-party-tab${declParty === "co_" ? " is-on" : ""}`} onClick={() => setDeclParty("co_")}>
                    {coName} <small>· {t("roleCoborrower")}</small> <small>{answeredCount("co_")}/14</small>
                  </button>
                </div>
              ) : null}
              <div style={{ display: declParty === "" ? "block" : "none" }}>{declarationsFor("")}</div>
              {showCo ? <div style={{ display: declParty === "co_" ? "block" : "none" }}>{declarationsFor("co_")}</div> : null}
            </>,
            <span className="gop-count">
              {t("answeredOf", { answered: answeredCount("") + (showCo ? answeredCount("co_") : 0), total: showCo ? 28 : 14 })}
            </span>
          )}

          {sectionCard(
            4,
            <>
              {t("secDocsLO")} <span className="gop-section-title-note">{t("optionalParen")}</span>
            </>,
            t("docsSubLO"),
            <>
              {showCo ? (
                <>
                  <div className="lop-upload-cols">
                    <div>
                      <div className="lop-upload-label">
                        {t("roleBorrower")}
                        <span>{borrowerName}</span>
                      </div>
                      {dropzone(makeDragger(fileList, setFileList))}
                      {fileRows(fileList, setFileList)}
                    </div>
                    <div>
                      <div className="lop-upload-label">
                        {t("roleCoborrower")}
                        <span>{coName}</span>
                      </div>
                      {dropzone(makeDragger(coFileList, setCoFileList))}
                      {fileRows(coFileList, setCoFileList)}
                    </div>
                  </div>
                  <div className="lop-note" style={{ marginTop: 10 }}>
                    {t("filesNote")}
                  </div>
                </>
              ) : (
                <>
                  {dropzone(makeDragger(fileList, setFileList))}
                  {fileRows(fileList, setFileList)}
                </>
              )}
              <div style={{ marginTop: 14 }}>{examples}</div>
            </>
          )}
        </Form>

        <div className="gop-submit-card">
          <p className="gop-consent">{t("nextNote")}</p>
          <button type="button" className="gop-btn-primary" disabled={saving} onClick={submit}>
            {saving ? t("savingApp") : t("continue")} <ArrowRightOutlined rev={undefined} />
          </button>
        </div>
      </main>
    </div>
  );
};

export default LOApplicationForm;
